import type { LinkedinRecruiterMessagingResponse, TransformedLinkedinRecruiterMessage } from './types';

import { useQuery } from '@tanstack/react-query';
import { browserExtensionClient } from 'clients/BrowserExtensionClient/BrowserExtensionClient';
import { crawlerClient } from 'clients/CrawlerClient/CrawlerClient';
import { notificationsClient } from 'clients/NotificationsClient/NotificationsClient';
import { useEffect, useRef, useState } from 'react';
import { encodeBase64 } from '../../../../helpers/base64';
import { useCurrentUserQuery } from '../useCurrentUserQuery';
import { fallbackSaveReceivedMessages } from './fallbackSaveReceivedMessages';
import { useLinkedinInMailConfig } from './linkedinInMail/useLinkedinInMailConfig';
import { MessageReceivedPlatform } from './platform.types';

const REFETCH_INTERVAL = 600 * 1000; // 10 minutes
const GET_UNREAD_LINKEDIN_RECRUITER_MESSAGES =
  'https://www.linkedin.com/talent/api/talentMailboxSummary?decoration=%28numUnseenMessages%2CinboxHomeUrl%2CrecentMessages*%28read%2CcreatedAt%2Csubject%2Cbody%2Curl%2CsenderFirstName%2CsenderLastName%2CsenderProfile~%28entityUrn%2CfirstName%2ClastName%2Cheadline%2CprofilePicture%2CvectorProfilePicture%2CpublicProfileUrl%2CfollowerCount%2CnetworkDistance%2CautomatedActionProfile%29%29%29';

export function useUnreadLinkedInRecruiterMessages() {
  const { data: currentUser } = useCurrentUserQuery();
  const { status: linkedinInMailStatus } = useLinkedinInMailConfig();

  const invalidationCountRef = useRef(0);
  const [shouldRunCountUnreadQuery, setShouldRunCountUnreadQuery] = useState(true);

  useQuery(
    ['countUnreadLinkedinRecruiterMessages'],
    async () => {
      const response = await browserExtensionClient.sendMessage('proxyHttpRequest', {
        url: GET_UNREAD_LINKEDIN_RECRUITER_MESSAGES,
        method: 'GET',
        addHeadersFrom: 'linkedInClassic',
        headers: {
          accept: 'application/json',
          'Accept-Encoding': 'gzip, deflate, br, zstd',
        },
      });

      return response;
    },
    {
      enabled: !!currentUser && linkedinInMailStatus === 'Logged In' && shouldRunCountUnreadQuery,
      refetchInterval: REFETCH_INTERVAL,
      refetchIntervalInBackground: true,
      onSuccess: async (data) => {
        if (currentUser) {
          const jsonData: LinkedinRecruiterMessagingResponse = JSON.parse(data.body);

          if (jsonData.status === 403 || jsonData.status === 400 || jsonData.status === 401) {
            if (invalidationCountRef.current < 3) {
              invalidationCountRef.current += 1;
            } else {
              setShouldRunCountUnreadQuery(false);
            }
            throw new Error(`HTTP ${jsonData.status} Error`);
          }

          const messages = await Promise.all(
            jsonData.recentMessages.map(async (message) => {
              const linkedinCandidateId = message.senderProfile.split(':ts_profile:')[1];

              try {
                const candidate = await crawlerClient.getLinkedinRecruiterCandidate(linkedinCandidateId);
                const id = candidate?.data.candidate?.linkedInMemberProfileUrnResolutionResult?.referenceUrn
                  ?.split(':')
                  .pop();
                const oldCandidateId = candidate.data.candidate?.candidate?.positionGroupView?.profileId;
                const profileId = oldCandidateId ? oldCandidateId : id;
                const esPersonId = `cvt-${profileId}`;

                return {
                  es_person_id: esPersonId,
                  message_id: String(message.createdAt),
                  thread_id: encodeBase64(`${currentUser?.id}-${esPersonId}`),
                  message_content: message.body,
                };
              } catch (error) {
                return null;
              }
            }),
          );

          const filteredMessages = messages.filter((message) => message !== null);

          const candidatesToProcess = await notificationsClient.saveLinkedinRecruiterMessagesNotifications({
            messages: filteredMessages,
          });

          if (candidatesToProcess.data.candidates.length > 0) {
            const transformedMessagesMap = new Map<string, TransformedLinkedinRecruiterMessage[]>();

            const messagesByPersonId = new Map<string, (typeof filteredMessages)[0]>();
            filteredMessages.forEach((msg) => {
              if (msg) messagesByPersonId.set(msg.es_person_id, msg);
            });

            jsonData.recentMessages.forEach((originalMessage, index) => {
              const filteredMessage = filteredMessages[index];
              if (filteredMessage) {
                const senderId =
                  originalMessage.senderProfile.split(':ts_profile:')[1] || originalMessage.senderProfile;

                const candidateId = filteredMessage.es_person_id.replace('cvt-', '');

                const fromSelf = candidateId !== senderId;

                const transformedMessage: TransformedLinkedinRecruiterMessage = {
                  senderId,
                  content: originalMessage.body,
                  fromSelf,
                  sentAt: originalMessage.createdAt,
                };

                const existingMessages = transformedMessagesMap.get(filteredMessage.es_person_id) || [];
                transformedMessagesMap.set(filteredMessage.es_person_id, [...existingMessages, transformedMessage]);
              }
            });

            for (const candidate of candidatesToProcess.data.candidates) {
              const transformedMessages = transformedMessagesMap.get(candidate.id) || [];

              if (transformedMessages.length === 0) continue;

              fallbackSaveReceivedMessages({
                currentUser,
                messages: transformedMessages,
                candidate,
                platform: MessageReceivedPlatform.LINKEDIN_RECRUITER,
              });
            }
          }
        }
      },
    },
  );

  useEffect(() => {
    if (currentUser) {
      invalidationCountRef.current = 0;
      setShouldRunCountUnreadQuery(true);
    }
  }, [currentUser, linkedinInMailStatus]);
}
